// A native Excel chart in a workbook that ExcelJS wrote. ExcelJS writes cells
// but not charts, so the chart's parts (the chart, a drawing that places it,
// their relationships and content types) are added to the xlsx zip after.
//
// comboChartXml draws stacked columns on the left axis and lines on the right,
// the shape of the Overview's labor graph; addChart puts a chart on a sheet.

import JSZip from 'jszip';

const NS_C = 'http://schemas.openxmlformats.org/drawingml/2006/chart';
const NS_A = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const NS_R = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const NS_XDR = 'http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing';
const REL_DRAWING = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing';
const REL_CHART = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart';
const CT_DRAWING = 'application/vnd.openxmlformats-officedocument.drawing+xml';
const CT_CHART = 'application/vnd.openxmlformats-officedocument.drawingml.chart+xml';

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// '#7CAFDD' -> '7CAFDD'
const hex = (c) => String(c || '').replace('#', '').toUpperCase();
const fill = (c) => `<a:solidFill><a:srgbClr val="${hex(c)}"/></a:solidFill>`;
const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

const strCache = (values) => `<c:strCache><c:ptCount val="${values.length}"/>${values
    .map((v, i) => (v === null || v === undefined || v === '' ? '' : `<c:pt idx="${i}"><c:v>${esc(v)}</c:v></c:pt>`)).join('')}</c:strCache>`;
// Blank points are left out of the cache, as Excel does for empty cells
const numCache = (values) => `<c:numCache><c:formatCode>General</c:formatCode><c:ptCount val="${values.length}"/>${values
    .map((v, i) => (isNum(v) ? `<c:pt idx="${i}"><c:v>${Number(v)}</c:v></c:pt>` : '')).join('')}</c:numCache>`;

const textPr = ({ sz = 800, color = '737373', b = false } = {}) =>
    `<c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="${sz}" b="${b ? 1 : 0}">${fill(color)}</a:defRPr></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr>`;

const axisTitle = (text, rot) =>
    `<c:title><c:tx><c:rich><a:bodyPr rot="${rot}" vert="horz"/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="900" b="1">${fill('737373')}</a:defRPr></a:pPr>` +
    `<a:r><a:rPr lang="en-US" sz="900" b="1">${fill('737373')}</a:rPr><a:t>${esc(text)}</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title>`;

const noLine = '<a:ln><a:noFill/></a:ln>';

/**
 * Chart XML: stacked columns on the left axis, lines on the right.
 * categories: { ref, values } (values: label per category, blank allowed);
 * bars: [{ name, nameRef, ref, values, color }];
 * lines: [{ name, nameRef, ref, values, color, dashed, markerOnly, label }]
 *   (markerOnly: a dot, no line; label: the point's value labelled with the series name);
 * gapWidth: % of a bar's width between bars; labelEvery: a category label every n categories;
 * yTitle / y2Title: the axis titles; yMax / y2Max: the axes' ends (Excel picks
 * when left out); numFmt: the axes' number format.
 */
export function comboChartXml({ categories, bars, lines = [], gapWidth = 50, labelEvery = 1, yTitle = '', y2Title = '', yMax, y2Max, numFmt = '#,##0' }) {
    const cat = `<c:cat><c:strRef><c:f>${esc(categories.ref)}</c:f>${strCache(categories.values)}</c:strRef></c:cat>`;
    const head = (s, i) => `<c:idx val="${i}"/><c:order val="${i}"/><c:tx><c:strRef><c:f>${esc(s.nameRef)}</c:f>${strCache([s.name])}</c:strRef></c:tx>`;
    const val = (s) => `<c:val><c:numRef><c:f>${esc(s.ref)}</c:f>${numCache(s.values)}</c:numRef></c:val>`;

    const barSer = bars.map((s, i) =>
        `<c:ser>${head(s, i)}<c:spPr>${fill(s.color)}${noLine}</c:spPr><c:invertIfNegative val="0"/>${cat}${val(s)}</c:ser>`).join('');

    const lineSer = lines.map((s, j) => {
        const i = bars.length + j;
        const stroke = s.markerOnly ? noLine
            : `<a:ln w="19050" cap="flat">${fill(s.color)}${s.dashed ? '<a:custDash><a:ds d="400000" sp="200000"/></a:custDash>' : '<a:prstDash val="solid"/>'}<a:round/></a:ln>`;
        const marker = s.markerOnly
            ? `<c:marker><c:symbol val="circle"/><c:size val="7"/><c:spPr>${fill(s.color)}<a:ln w="9525">${fill(s.color)}</a:ln></c:spPr></c:marker>`
            : '<c:marker><c:symbol val="none"/></c:marker>';
        const label = s.label
            ? `<c:dLbls><c:numFmt formatCode="${esc(`${numFmt} "hrs"`)}" sourceLinked="0"/><c:spPr>${fill('F3F4F6')}<a:ln w="9525">${fill('9CA3AF')}</a:ln></c:spPr>` +
              `${textPr({ sz: 800, color: '374151', b: true })}<c:dLblPos val="l"/><c:showLegendKey val="0"/><c:showVal val="1"/><c:showCatName val="0"/>` +
              '<c:showSerName val="1"/><c:showPercent val="0"/><c:showBubbleSize val="0"/><c:separator>\n</c:separator></c:dLbls>'
            : '';
        return `<c:ser>${head(s, i)}<c:spPr>${stroke}</c:spPr>${marker}${label}${cat}${val(s)}<c:smooth val="0"/></c:ser>`;
    }).join('');

    const axisText = textPr({ sz: 800 });
    const valAxis = (id, crossId, pos, title, rot, crosses, grid, max) =>
        `<c:valAx><c:axId val="${id}"/><c:scaling><c:orientation val="minMax"/>${isNum(max) ? `<c:max val="${Number(max)}"/>` : ''}<c:min val="0"/></c:scaling>` +
        `<c:delete val="0"/><c:axPos val="${pos}"/>` +
        (grid ? `<c:majorGridlines><c:spPr><a:ln w="9525">${fill('F1F5F9')}</a:ln></c:spPr></c:majorGridlines>` : '') +
        `${title ? axisTitle(title, rot) : ''}<c:numFmt formatCode="${esc(numFmt)}" sourceLinked="0"/><c:majorTickMark val="none"/><c:minorTickMark val="none"/>` +
        `<c:tickLblPos val="nextTo"/><c:spPr>${noLine}</c:spPr>${axisText}<c:crossAx val="${crossId}"/><c:crosses val="${crosses}"/><c:crossBetween val="between"/></c:valAx>`;

    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
        `<c:chartSpace xmlns:c="${NS_C}" xmlns:a="${NS_A}" xmlns:r="${NS_R}"><c:date1904 val="0"/><c:roundedCorners val="0"/>` +
        '<c:chart><c:autoTitleDeleted val="1"/><c:plotArea><c:layout/>' +
        `<c:barChart><c:barDir val="col"/><c:grouping val="stacked"/><c:varyColors val="0"/>${barSer}<c:gapWidth val="${gapWidth}"/><c:overlap val="100"/>` +
        '<c:axId val="500000001"/><c:axId val="500000002"/></c:barChart>' +
        (lines.length ? `<c:lineChart><c:grouping val="standard"/><c:varyColors val="0"/>${lineSer}<c:marker val="1"/><c:axId val="500000003"/><c:axId val="500000004"/></c:lineChart>` : '') +
        '<c:catAx><c:axId val="500000001"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/><c:axPos val="b"/>' +
        `<c:numFmt formatCode="General" sourceLinked="1"/><c:majorTickMark val="none"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/>` +
        `<c:spPr><a:ln w="9525">${fill('E5E5E5')}</a:ln></c:spPr>${axisText}<c:crossAx val="500000002"/><c:crosses val="autoZero"/>` +
        `<c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/><c:tickLblSkip val="${labelEvery}"/><c:noMultiLvlLbl val="0"/></c:catAx>` +
        valAxis(500000002, 500000001, 'l', yTitle, -5400000, 'autoZero', true, yMax) +
        (lines.length
            ? '<c:catAx><c:axId val="500000003"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="1"/><c:axPos val="b"/>' +
              '<c:majorTickMark val="none"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:crossAx val="500000004"/><c:crosses val="autoZero"/>' +
              '<c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/><c:noMultiLvlLbl val="0"/></c:catAx>' +
              valAxis(500000004, 500000003, 'r', y2Title, 5400000, 'max', false, y2Max)
            : '') +
        `<c:spPr><a:noFill/>${noLine}</c:spPr></c:plotArea>` +
        `<c:legend><c:legendPos val="b"/><c:overlay val="0"/>${textPr({ sz: 800, color: '0A0A0A', b: true })}</c:legend>` +
        // span: a line runs on across the categories it has no point in
        '<c:plotVisOnly val="1"/><c:dispBlanksAs val="span"/></c:chart>' +
        `<c:spPr>${fill('FFFFFF')}${noLine}</c:spPr>${textPr({ sz: 900, color: '0A0A0A' })}</c:chartSpace>`;
}

const attrs = (tag) => {
    const out = {};
    tag.replace(/([\w:]+)="([^"]*)"/g, (_, k, v) => { out[k] = v; return ''; });
    return out;
};
const relsXml = (rels) => '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rels.map((r) =>
        `<Relationship Id="${r.Id}" Type="${r.Type}" Target="${r.Target}"/>`).join('')}</Relationships>`;
const readRels = (xml) => (xml.match(/<Relationship\b[^>]*>/g) || []).map(attrs);
const nextNo = (zip, re) => 1 + Math.max(0, ...Object.keys(zip.files).map((p) => Number((re.exec(p) || [])[1] || 0)));

/**
 * The xlsx with chartXml placed on the named sheet, as a Uint8Array.
 * from / to: { col, row }, 0-based cells for the chart's top-left and bottom-right corners.
 */
export async function addChart(xlsx, { sheet, chartXml, from, to, name = 'Chart' }) {
    const zip = await JSZip.loadAsync(xlsx);

    // The sheet's part, through workbook.xml and its relationships
    const wbXml = await zip.file('xl/workbook.xml').async('string');
    const sheetTag = (wbXml.match(/<sheet\b[^>]*>/g) || []).map(attrs).find((a) => a.name === esc(sheet));
    if (!sheetTag) throw new Error(`addChart: no sheet named ${sheet}`);
    const wbRels = readRels(await zip.file('xl/_rels/workbook.xml.rels').async('string'));
    const target = wbRels.find((r) => r.Id === sheetTag['r:id']).Target.replace(/^\/?(xl\/)?/, '');
    const sheetPath = `xl/${target}`;
    const sheetFile = sheetPath.split('/').pop();
    const sheetRelsPath = `xl/worksheets/_rels/${sheetFile}.rels`;

    const chartNo = nextNo(zip, /^xl\/charts\/chart(\d+)\.xml$/);
    const drawingNo = nextNo(zip, /^xl\/drawings\/drawing(\d+)\.xml$/);

    zip.file(`xl/charts/chart${chartNo}.xml`, chartXml);
    zip.file(`xl/drawings/drawing${drawingNo}.xml`, '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
        `<xdr:wsDr xmlns:xdr="${NS_XDR}" xmlns:a="${NS_A}"><xdr:twoCellAnchor editAs="oneCell">` +
        `<xdr:from><xdr:col>${from.col}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${from.row}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>` +
        `<xdr:to><xdr:col>${to.col}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${to.row}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>` +
        `<xdr:graphicFrame macro=""><xdr:nvGraphicFramePr><xdr:cNvPr id="2" name="${esc(name)}"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr>` +
        '<xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>' +
        `<a:graphic><a:graphicData uri="${NS_C}"><c:chart xmlns:c="${NS_C}" xmlns:r="${NS_R}" r:id="rId1"/></a:graphicData></a:graphic>` +
        '</xdr:graphicFrame><xdr:clientData/></xdr:twoCellAnchor></xdr:wsDr>');
    zip.file(`xl/drawings/_rels/drawing${drawingNo}.xml.rels`, relsXml([{ Id: 'rId1', Type: REL_CHART, Target: `../charts/chart${chartNo}.xml` }]));

    // The sheet points at the drawing
    const sheetRels = zip.file(sheetRelsPath) ? readRels(await zip.file(sheetRelsPath).async('string')) : [];
    const relId = `rId${1 + Math.max(0, ...sheetRels.map((r) => Number(String(r.Id).replace(/\D/g, '')) || 0))}`;
    zip.file(sheetRelsPath, relsXml([...sheetRels, { Id: relId, Type: REL_DRAWING, Target: `../drawings/drawing${drawingNo}.xml` }]));

    let sheetXml = await zip.file(sheetPath).async('string');
    if (/<drawing\b/.test(sheetXml)) throw new Error(`addChart: sheet ${sheet} already has a drawing`);
    if (!/xmlns:r=/.test(sheetXml.slice(0, sheetXml.indexOf('>', sheetXml.indexOf('<worksheet'))))) {
        sheetXml = sheetXml.replace('<worksheet', `<worksheet xmlns:r="${NS_R}"`);
    }
    // <drawing> goes after the page setup and before legacy drawings, tables and extLst
    const at = sheetXml.search(/<(legacyDrawing|legacyDrawingHF|drawingHF|picture|oleObjects|controls|webPublishItems|tableParts|extLst)\b|<\/worksheet>/);
    sheetXml = `${sheetXml.slice(0, at)}<drawing r:id="${relId}"/>${sheetXml.slice(at)}`;
    zip.file(sheetPath, sheetXml);

    let types = await zip.file('[Content_Types].xml').async('string');
    types = types.replace('</Types>',
        `<Override PartName="/xl/drawings/drawing${drawingNo}.xml" ContentType="${CT_DRAWING}"/>` +
        `<Override PartName="/xl/charts/chart${chartNo}.xml" ContentType="${CT_CHART}"/></Types>`);
    zip.file('[Content_Types].xml', types);

    return zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
}
